'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/auth-context';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TaskSelectionModal } from './task-selection-modal';
import { EssayEditorContent } from './essay-editor-content';
import { EssayEditorSidebar } from './essay-editor-sidebar';
import { User } from 'firebase/auth';
import { QuestionType, QuestionNote } from '@/types/task-bank';
import { EssayEditorProps, StructuredEssayData } from '@/types/writing';
import { useEssayEditorState } from '@/hooks/use-essay-editor-state';
import { SlashCommandMenu } from './slash-command-menu';
import { CommandResultPopover } from './command-result-popover';
import { toast } from 'sonner';
import { ScoreDisplay } from './score-display';
import { EssayScoreResult } from '@/app/score/types/score';

function isFirebaseUser(u: unknown): u is User {
  return !!u && typeof u === 'object' && 'uid' in u && 'getIdToken' in u;
}

export function EssayEditor({
  initialPrompt = '',
  initialIntroduction = '',
  initialBodyParagraphs = [
    { type: '', content: '' },
    { type: '', content: '' },
  ],
  initialConclusion = '',
  initialEssayPlan,
  initialEssayId = null,
  onSuggestionSuccess,
  suggestionClearTrigger,
}: EssayEditorProps) {
  const { user, userProfile } = useAuth();

  const state = useEssayEditorState({
    initialPrompt,
    initialIntroduction,
    initialBodyParagraphs,
    initialConclusion,
    initialEssayPlan: initialEssayPlan,
    initialEssayId,
    onSuggestionSuccess,
    suggestionClearTrigger,
    user: isFirebaseUser(user) ? user : null,
    userProfile,
    taskType: 'task2',
  });

  const structuredEssayData: StructuredEssayData = useMemo(
    () => ({
      type: 'essay',
      introduction: state.introduction,
      bodyParagraphs: state.bodyParagraphs,
      conclusion: state.conclusion,
    }),
    [state.introduction, state.bodyParagraphs, state.conclusion]
  );

  const currentQuestionType = state.taskBank.selectedTask?.questionType as
    | QuestionType
    | ''
    | undefined;
  const currentQuestionNote = state.taskBank.selectedTask?.questionNote as
    | QuestionNote
    | ''
    | undefined;

  const [isScoreSheetOpen, setIsScoreSheetOpen] = useState(false);

  // Cast score to EssayScoreResult to access the changeType property
  const scoreResult = state.score as EssayScoreResult | null;

  // Effect for global Cmd+K scoring shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.shiftKey) return;
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      )
        return;

      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (
          state.isProUser &&
          state.scoringRequirementsList.length === 0 &&
          !state.isScoring
        ) {
          state.fetchNewScore(
            state.prompt,
            structuredEssayData,
            'task2',
            currentQuestionType,
            currentQuestionNote
          );
          setIsScoreSheetOpen(true);
        } else if (!state.isProUser) {
          toast.error('AI Scoring Disabled', {
            description: 'Keyboard shortcut requires a Pro account.',
          });
        } else if (state.scoringRequirementsList.length > 0) {
          toast.error('Cannot Score Yet', {
            description: state.scoringRequirementsList.join('. '),
          });
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [
    state,
    structuredEssayData,
    currentQuestionType,
    currentQuestionNote,
    setIsScoreSheetOpen,
  ]);

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          'flex flex-col md:flex-row gap-4 flex-grow',
          'min-h-[calc(100vh-200px)]'
        )}
      >
        {/* Left column - Essay Editor */}
        <div className='w-full md:w-1/2 flex flex-col space-y-4'>
          <EssayEditorContent
            hasSelectedTask={!!state.taskBank.selectedTask}
            introduction={state.introduction}
            bodyParagraphs={state.bodyParagraphs}
            conclusion={state.conclusion}
            onIntroductionChange={state.handleIntroductionChange}
            onConclusionChange={state.handleConclusionChange}
            onBodyParagraphChange={state.handleBodyParagraphChange}
            onBodyTypeChange={state.handleBodyTypeChange}
            onAddBodyParagraph={state.addBodyParagraph}
            onRemoveBodyParagraph={state.removeBodyParagraph}
            isSlashMenuOpen={state.isSlashMenuOpen}
            handleSlashTrigger={state.handleSlashTrigger}
            handleSlashFilterUpdate={state.handleSlashFilterUpdate}
            handleSlashClose={state.handleSlashClose}
            handleTextareaKeyDown={state.handleSlashCommandTextareaKeyDown}
            saveStatus={state.saveStatus}
            totalWordCount={state.totalWordCount}
            currentContent={state.currentContent}
            initialPrompt={initialPrompt}
          />
        </div>

        {/* Right column - Task selector and controls */}
        <div className='w-full md:w-1/2 flex flex-col space-y-4'>
          <EssayEditorSidebar
            hasSelectedTask={!!state.taskBank.selectedTask}
            selectedTask={state.taskBank.selectedTask}
            user={isFirebaseUser(state.user) ? state.user : null}
            userProfile={userProfile}
            prompt={state.prompt}
            showIdeas={state.taskBank.showIdeas}
            setShowIdeas={state.taskBank.setShowIdeas}
            setTaskBankOpen={state.taskBank.setTaskBankOpen}
            isScoring={state.isScoring}
            essayData={structuredEssayData}
            currentEssayId={state.currentEssayId}
            fetchNewScore={state.fetchNewScore}
            canScore={state.scoringRequirementsList.length === 0}
            requirements={state.scoringRequirementsList}
            essayPlan={state.essayPlan}
            handlePlanTypeChange={state.handlePlanTypeChange}
            handlePlanContentChange={state.handlePlanContentChange}
            addPlanItem={state.addPlanItem}
            removePlanItem={state.removePlanItem}
            handleKeyDown={state.handleSlashCommandTextareaKeyDown}
            isPlanOpen={state.isPlanOpen}
            onPlanOpenChange={state.setIsPlanOpen}
            currentQuestionType={currentQuestionType}
            currentQuestionNote={currentQuestionNote}
          />
        </div>

        <SlashCommandMenu
          isOpen={state.isSlashMenuOpen}
          targetElement={state.slashMenuTargetElement}
          commands={state.slashMenuFilteredCommands}
          selectedIndex={state.slashMenuSelectedIndex}
          onSelectCommand={(cmdId) => state.executeSlashCommand(cmdId)}
          onClose={state.handleSlashClose}
          onHoverIndex={state.setSlashMenuSelectedIndex}
        />

        <CommandResultPopover
          commandId={state.activeCommandId}
          isOpen={state.isCommandResultPopoverOpen}
          isLoading={state.isCommandLoading}
          error={state.commandError}
          result={state.commandResult}
          targetElement={state.commandResultTargetElement}
          onClose={state.closeCommandResultPopover}
        />

        <AlertDialog
          open={state.showMinimalChangeDialog}
          onOpenChange={state.setShowMinimalChangeDialog}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {scoreResult?.changeType === 'no_change'
                  ? 'No Changes Detected'
                  : 'Minimal Changes Detected'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {scoreResult?.changeType === 'no_change'
                  ? 'Your essay has not changed since the last scoring. Please make edits to get an updated score.'
                  : 'Your essay has only minor changes since the last scoring. Please make more substantial edits to get an updated score.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={() => state.setShowMinimalChangeDialog(false)}
              >
                Okay, I&apos;ll Edit More
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <TaskSelectionModal
          open={state.taskBank.taskBankOpen}
          onOpenChange={(open: boolean) => state.taskBank.setTaskBankOpen(open)}
          onSelectTask={state.taskBank.handleSelectTask}
          selectedTaskId={state.taskBank.selectedTask?.id || null}
          taskType='task2'
        />
      </div>

      <ScoreDisplay
        score={state.score}
        isOpen={isScoreSheetOpen}
        onOpenChange={setIsScoreSheetOpen}
        isScoring={state.isScoring}
        scoreError={state.scoreError}
        taskType='task2'
        onRefresh={() => {
          if (state.scoringRequirementsList.length === 0) {
            state.fetchNewScore(
              state.prompt,
              structuredEssayData,
              'task2',
              currentQuestionType,
              currentQuestionNote
            );
          }
        }}
      />
    </TooltipProvider>
  );
}
